// ELVA's work — big "Log" history fixture (00-ds-09 §3, D148, lists at scale).
// ~1,500 deterministic log entries so the Log can actually page (50/page),
// search and date-filter client-side. Real data comes from the server (§8).

import { generate, pick, int, money, fullName, PAYERS, usd, MOCK_NOW } from '@/components/lists';
import type { LogEntry, LogKind, DoneBy } from './types';
import { PMS } from '@/features/shared/pms';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dayLabel(d: Date): string {
  return `${MONTHS[d.getMonth()] ?? 'Jan'} ${String(d.getDate())}, ${String(d.getFullYear())}`;
}

/** A US 12-hour clock label within working hours, e.g. "10:41 AM". */
function timeLabel(r: () => number): string {
  const h24 = int(r, 7, 18);
  const m = int(r, 0, 59);
  const ap = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(h12)}:${String(m).padStart(2, '0')} ${ap}`;
}

const KINDS: { kind: LogKind; label: string }[] = [
  { kind: 'posted', label: 'Posted' },
  { kind: 'call', label: 'Call' },
  { kind: 'portal', label: 'Portal' },
  { kind: 'document', label: 'Document' },
  { kind: 'claims', label: 'Claims' },
  { kind: 'appeal', label: 'Appeal' },
  { kind: 'your_team', label: 'Your team' },
];

const ACTORS = ['Ava Thompson', 'Ryan Cole', 'Maria Delgado', 'Dana Fisher'] as const;

function row(r: () => number, kind: LogKind): Pick<LogEntry, 'what' | 'meta' | 'how' | 'result' | 'resultBad' | 'why'> {
  const name = fullName(r);
  const payer = pick(r, PAYERS);
  const amt = money(r, 80, 2400);
  const claim = `R-26-${String(int(r, 10000, 99999))}`;
  switch (kind) {
    case 'posted':
      return { what: `Posted ${payer}’s payment to ${name}`, meta: `${payer} · ${usd(amt)} · ${claim}`, how: 'From the 835 ERA', result: 'Matched to the ledger', why: `ELVA matched the remittance line to ${name}’s open claim and posted it in ${PMS}.` };
    case 'call':
      return { what: `Called ${payer} about ${name}’s claim`, meta: `${payer} · ${claim}`, how: 'Automated call', result: pick(r, ['Reprocessed', 'Payment confirmed', 'Left a message']), why: `The claim had been pending past the payer’s usual turnaround, so ELVA called to move it.` };
    case 'portal':
      return { what: `Checked ${name}’s status on the ${payer} portal`, meta: `${payer} · ${claim}`, how: 'Signed in to the portal', result: pick(r, ['Status read', 'EOB downloaded']), why: `ELVA signed in to the payer portal to read the claim status and pull the EOB.` };
    case 'document':
      return { what: `Read an EOB from ${payer}`, meta: `${payer} · ${usd(amt)}`, how: 'Read the document', result: 'Filed', why: `ELVA read the scanned EOB, matched it to the claim, and filed it.` };
    case 'claims':
      return { what: `Sent ${name}’s claim to ${payer}`, meta: `${payer} · ${usd(amt)} · ${claim}`, how: 'Sent through the clearinghouse', result: pick(r, ['Accepted', 'Queued']), why: `The visit was checked out and coded, so ELVA sent the claim.` };
    case 'appeal':
      return { what: `Filed an appeal with ${payer} for ${name}`, meta: `${payer} · ${usd(amt)} · ${claim}`, how: pick(r, ['Payer website', 'Fax']), result: pick(r, ['Submitted', 'Confirmed received']), why: `The claim was denied for a reason ELVA can rebut, so it filed the appeal with the records attached.` };
    case 'your_team':
      return { what: `${pick(r, ACTORS)} changed what ELVA did on ${name}’s claim`, meta: `${payer} · ${claim}`, how: 'By a person', result: 'Noted', resultBad: false, why: `A teammate stepped in and adjusted ELVA’s action.` };
  }
}

/**
 * ~1,500 entries spread across the last ~150 days (newest first). Weighted so
 * the current month still holds a few hundred rows for the default window.
 */
export const LOG_HISTORY: LogEntry[] = generate<LogEntry>(1500, 0x109, (r, i) => {
  const { kind, label } = pick(r, KINDS);
  const daysBack = int(r, 0, 150);
  const d = new Date(MOCK_NOW);
  d.setDate(d.getDate() - daysBack);
  const iso = d.toISOString().slice(0, 10);
  const doneBy: DoneBy = kind === 'your_team' ? 'person' : r() < 0.28 ? 'confirmed' : 'own';
  const by = doneBy === 'person' ? `${pick(r, ACTORS)} (Lone Star Billing)` : doneBy === 'confirmed' ? `ELVA · confirmed by ${pick(r, ACTORS)}` : 'ELVA';
  const base = row(r, kind);
  return {
    id: `logh-${String(i)}`,
    time: timeLabel(r),
    day: dayLabel(d),
    dateIso: iso,
    kind,
    kindLabel: label,
    ...base,
    by,
    doneBy,
    title: base.what,
    headerMeta: `${base.meta} · ${dayLabel(d)}`,
  };
}).sort((a, b) => (a.dateIso && b.dateIso && a.dateIso !== b.dateIso ? b.dateIso.localeCompare(a.dateIso) : 0));
